/* eslint-disable jsx-a11y/anchor-is-valid */
import { map, mapObject, constant } from 'underscore';
import PropTypes from 'prop-types';
import React, { useContext, useMemo } from 'react';
import classNames from 'classnames';

import { withDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { LearnMoreLink } from '@bingads-webui-campaign-react/help';
import { Link } from '@fluentui/react';

import { TagSVG } from '../components/tag-SVG';
import { senders, supportTeamLinks, useHelpIds } from '../utils/email-tag';
import { getEmailTemplateStyle, emailTemplateWidth } from '../styles/email-template-styles';

const EmailTemplateUnstyled = (props) => {
  const {
    tag,
    classes,
  } = props;
  const {
    type,
  } = tag;
  const i18n = useContext(I18nContext);
  const helpIds = useHelpIds();

  const onRenderDescriptions = useMemo(() => {
    const texts = i18n.getString(_TL_('Hello from {{send}},'), { send: senders[type] });
    const texts2 = i18n.getString(_TL_('{{username}} sent the tracking tag below to you, so you can add it to their webpages. This tag will help them track customer actions on their website.'), { username: `{${senders[type]} ${i18n.getString(_TL_('username'))}}` });
    return (
      <div className="description">
        <div className="description-1">
          {texts}
        </div>
        <div className={classNames(classes.text, 'description-2')}>
          {texts2}
        </div>
      </div>
    );
  }, [type, i18n, classes.text]);

  const title = useMemo(() => _TL_('To add the tag to their website:'), []);
  const stepList = useMemo(() => [
    _TL_('Copy the tag below to your clipboard.'),
    _TL_('Open the HTML for each page on the website.'),
    _TL_('Paste the tracking tag anywhere in the head section of the pages\' code (between the HTML "<head>" and "</head>" tags).'),
    _TL_('Make sure the tracking tag is in the code for every page of the website.'),
    _TL_('Save the changes to the webpages.'),
  ], []);
  const onRenderSteps = useMemo(() => map(stepList, (step, idx) => (
    <li key={idx} className={`step-${idx}`}>
      {i18n.getString(step)}
    </li>
  )), [stepList, i18n]);
  const onRenderInstructions = useMemo(() => (
    <div className={classNames(classes.instruction, 'instruction')}>
      <div className="title">
        {i18n.getString(title)}
      </div>
      <ol className={classNames(classes.steps, 'instruction-steps')}>
        {onRenderSteps}
      </ol>
    </div>
  ), [i18n, title, onRenderSteps, classes.instruction, classes.steps]);

  const onRenderTagDescriptions = useMemo(() => {
    const texts = i18n.getString(_TL_('For more information on adding tracking tags to webpages - including instructions for WordPress, Wix, Shopify, and BigCommerce - {0}.')).split('{0}');
    const texts2 = i18n.getString(_TL_('Please contact the {0} if you have any questions. Thank you for using Microsoft Advertising.')).split('{0}');

    return (
      <div className="tag-description">
        <div className={classNames(classes.text, 'description-1')}>
          {texts[0]}
          <LearnMoreLink
            topic={`${helpIds[type]}`}
            linkText={i18n.getString(_TL_('take a look at this article'))}
          />
          {texts[1]}
        </div>
        <div className={classNames(classes.text, 'description-2')}>
          {texts2[0]}
          <Link
            href={supportTeamLinks[type]}
            target="_blank"
          >
            {`${senders[type]} ${i18n.getString(_TL_('Support team'))}`}
          </Link>
          {texts2[1]}
        </div>
      </div>
    );
  }, [i18n, classes.text, helpIds, type]);

  const onRenderAppreciation = useMemo(() => {
    const texts = i18n.getString(_TL_('The {{sender}} team'), { sender: senders[type] });
    return (
      <div className={classNames(classes.text, 'appreciation')}>
        <div className="appreciation-1">
          {i18n.getString(_TL_('Kind regards,'))}
        </div>
        <div className="appreciation-2">
          {texts}
        </div>
      </div>
    );
  }, [type, i18n, classes.text]);

  const templateStyle = useMemo(() => ({
    width: emailTemplateWidth[type],
  }), [type]);

  return (
    <div className={classNames(classes.template, 'email-template')} style={templateStyle}>
      {onRenderDescriptions}
      {onRenderInstructions}
      <div className="tag-title">
        {i18n.getString(_TL_('Tracking tag:'))}
      </div>
      <TagSVG
        tag={tag}
        renderImage={false}
      />
      {onRenderTagDescriptions}
      {onRenderAppreciation}
    </div>
  );
};

EmailTemplateUnstyled.propTypes = {
  tag: PropTypes.shape({
    id: PropTypes.number.isRequired,
    type: PropTypes.oneOf(['Container', 'UET']).isRequired,
  }).isRequired,
  classes: PropTypes.shape(mapObject(
    getEmailTemplateStyle(),
    constant(PropTypes.string.isRequired)
  )).isRequired,
};

export const EmailTemplate = withDefaultStyles(
  EmailTemplateUnstyled,
  getEmailTemplateStyle
);
